import { Stack, Typography } from '@mui/material';

import { Button } from '@/components/form/baseInputs/Button';
import { IconButton } from '@/components/form/baseInputs/Button/IconButton';
import { XCloseIcon } from '@/components/icons/XCloseIcon';
import { Callout } from '@/components/notifications/Callout/Callout';

export enum ReviewSuggestionsBannerVariant {
  Default = 'default',
  Overview = 'overview',
}

interface ReviewSuggestionsBannerBaseProps {
  onDismiss: () => void;
  onReview: () => void;
}

type ReviewSuggestionsBannerProps = ReviewSuggestionsBannerBaseProps &
  (
    | {
        variant?: ReviewSuggestionsBannerVariant.Default;
      }
    | {
        suggestionCount: number;
        variant: ReviewSuggestionsBannerVariant.Overview;
      }
  );

function getOverviewSubtext(suggestionCount: number) {
  const countText =
    suggestionCount === 1 ? 'one suggestion' : `${suggestionCount} suggestions`;

  return `Luminary has ${countText} from documents. Manage these suggestions to build this household.`;
}

export function ReviewSuggestionsBanner(props: ReviewSuggestionsBannerProps) {
  const { onDismiss, onReview } = props;
  const isOverview = props.variant === ReviewSuggestionsBannerVariant.Overview;
  const header = isOverview ? 'Review suggestions' : 'Suggestions found';
  const subtext = isOverview
    ? getOverviewSubtext(props.suggestionCount)
    : 'Luminary AI has found multiple suggestions. Review and confirm to populate balance sheet.';

  return (
    <Callout severity="success" skipTextWrapper>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        gap={2}
        width="100%"
      >
        <Stack direction="column">
          <Typography variant="title-xs" paddingBottom={0.5}>
            {header}
          </Typography>
          <Typography display="block" variant="body-xs" paddingBottom={1}>
            {subtext}
          </Typography>
          <Button
            size="xs"
            variant="primary"
            onClick={onReview}
            sx={{ alignSelf: 'flex-start' }}
          >
            Review suggestions
          </Button>
        </Stack>
        <IconButton
          variant="transparent"
          size="sm"
          icon={XCloseIcon}
          ariaLabel="Dismiss"
          onClick={onDismiss}
        />
      </Stack>
    </Callout>
  );
}
